import { useCallback, useEffect, useState } from 'react'
import { ChevronDown, ChevronRight, ShieldAlert } from 'lucide-react'
import { cn } from '@/lib/utils'
import { explain } from '@/shared/api/problem'
import { ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import { listCouncils, listPlaybooks, type Council, type Playbook } from './api'
import { CATEGORY_LABEL, STAGE_BLURB, STAGE_KIND_LABEL } from './vocab'

/**
 * The governed prompt library.
 *
 * ## Why the prompts are readable here
 *
 * A playbook decides what the organisation is told about its own election. Anyone
 * who can read a report should be able to read the instruction that produced it —
 * otherwise the reports are authoritative for a reason nobody in the room can
 * inspect.
 *
 * What is NOT shown, because it is not in the row: the output contract. The
 * schema the editor must satisfy is code-owned, so an operator tuning a mission
 * statement cannot change the shape of the answer and break the parser that reads
 * it.
 */
export function PlaybooksPage() {
  const [playbooks, setPlaybooks] = useState<Playbook[] | null>(null)
  const [councils, setCouncils] = useState<Council[]>([])
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setError(null)
      const [p, c] = await Promise.all([listPlaybooks(), listCouncils()])
      setPlaybooks(p)
      setCouncils(c)
    } catch (err) {
      setError(explain(err))
    }
  }, [])

  useEffect(() => { void load() }, [load])

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-erp-lg font-semibold">Playbooks &amp; councils</h1>
        <p className="text-erp-xs text-muted-foreground mt-0.5">
          A playbook owns the mission and the method. The output contract is owned by the code,
          so editing a prompt cannot break the parser that reads the answer.
        </p>
      </div>

      <ErrorStrip message={error} className="rounded-md border" />
      {playbooks === null && !error && <LoadingBlock />}

      <section className="space-y-2">
        <h2 className="text-erp-sm font-medium">Councils</h2>
        <div className="grid gap-2"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
          {councils.map((council) => (
            <div key={council.code} className="rounded-md border border-border px-3 py-2.5">
              <div className="text-erp-sm font-medium">{council.title}</div>
              <p className="text-erp-xs text-muted-foreground mt-0.5">{council.description}</p>
              <ol className="mt-1.5 space-y-1">
                {council.stages.map((stage) => (
                  <li key={stage.key} className="text-erp-xs">
                    <span className="font-medium">
                      {STAGE_KIND_LABEL[stage.kind] ?? stage.kind}
                    </span>
                    {stage.vendorDiverseFrom.length > 0 && (
                      <span className="ml-1 text-amber-700 dark:text-amber-400">
                        must differ in vendor from {stage.vendorDiverseFrom.join(', ')}
                      </span>
                    )}
                    <div className="text-muted-foreground">{STAGE_BLURB[stage.kind]}</div>
                    <div className="text-muted-foreground/70">
                      needs {stage.capabilities.join(', ') || 'nothing in particular'}
                      {stage.fanOut && ` · fans out over the brief's sections`}
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      </section>

      <section className="space-y-2">
        <h2 className="text-erp-sm font-medium">Playbooks</h2>
        <ul className="space-y-1.5">
          {(playbooks ?? []).map((playbook) => (
            <li key={playbook.code} className="rounded-md border border-border">
              <button type="button"
                onClick={() => setOpen(open === playbook.code ? null : playbook.code)}
                className="flex w-full items-start gap-2 px-3 py-2 text-left">
                {open === playbook.code
                  ? <ChevronDown className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  : <ChevronRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-erp-sm font-medium">{playbook.title}</span>
                    <span className="text-erp-xs text-muted-foreground">
                      {CATEGORY_LABEL[playbook.category] ?? playbook.category}
                    </span>
                    <span className="text-erp-xs text-muted-foreground">
                      {playbook.councilCode.toLowerCase()}
                    </span>
                    {playbook.sensitivity === 'SENSITIVE' && (
                      <span className="inline-flex items-center gap-1 rounded border
                        border-amber-200 bg-amber-50 px-1.5 py-px text-erp-xs text-amber-700
                        dark:bg-amber-500/10 dark:text-amber-400">
                        <ShieldAlert className="h-3 w-3" /> sensitive
                      </span>
                    )}
                  </div>
                  <p className="text-erp-xs text-muted-foreground mt-0.5">{playbook.summary}</p>
                </div>
              </button>

              {open === playbook.code && (
                <div className="border-t border-border px-3 py-2 space-y-2">
                  <div className="text-erp-xs text-muted-foreground">
                    Needs <code className="rounded bg-muted px-1">
                      {playbook.requiredAuthority}</code>{' '}
                    · reads {playbook.contributors.join(', ') || 'everything in scope'}
                  </div>
                  <div>
                    <div className="text-erp-xs font-medium uppercase tracking-wide
                      text-muted-foreground">
                      The instruction
                    </div>
                    <pre className={cn('mt-1 whitespace-pre-wrap rounded-md border border-border',
                      'bg-muted/20 px-2.5 py-2 text-erp-xs leading-relaxed')}>
                      {playbook.systemPrompt}
                    </pre>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      </section>
    </div>
  )
}
